import {
  isResolved,
  type ValidationSubmission,
} from "@/lib/validationSubmission";
import {
  InteractiveElementStatusKind,
  type InteractiveElementStatus,
} from "@/lib/interactiveElementState";
import type { WorksheetAnswerRecord } from "@/lib/worksheetAnswers";
import type {
  WorksheetAnswerState,
  WorksheetRunnerState,
} from "@/components/storyboard/worksheet/WorksheetRunner";
import type { WorksheetData, WorksheetTaskData } from "@/types/storyboard";

/**
 * Content-Factory worksheet tasks have no stable `id`, so one is derived from
 * the list and position. Answers are keyed by task id; without this every task
 * collides on the same (undefined) key. Provided ids are kept as they are.
 */
export const withTaskKeys = (
  tasks: WorksheetTaskData[],
  prefix: string
): WorksheetTaskData[] =>
  tasks.map((task, i) => (task.id ? task : { ...task, id: `${prefix}-${i}` }));

/**
 * Card status of a worksheet, read off the learner's saved task submissions.
 * The server keeps every answer and verdict — the slot index is the task's
 * position in the main + additional list — so a reload, or another device,
 * meets the worksheet where it was left.
 *
 * A task is settled once it is right or out of attempts. The worksheet is done
 * when every main task is settled and either there are no additional tasks or
 * the learner took them all on. Declining the additional tasks leaves nothing
 * on the server, so a worksheet stopped at that offer shows the offer again.
 */
export const resolveWorksheetStatus = (
  submissions: Record<number, ValidationSubmission>,
  data: WorksheetData
): InteractiveElementStatus | undefined => {
  if (Object.keys(submissions).length === 0) return undefined;

  const mainTasks = withTaskKeys(data.tasks ?? [], "main");
  const bonusTasks = withTaskKeys(data.additionalTasks ?? [], "bonus");
  const tasks = [...mainTasks, ...bonusTasks];

  const answers: Record<string, WorksheetAnswerState> = {};
  const isSettled = (index: number): boolean => {
    const submission = submissions[index];
    return submission != null && isResolved(submission);
  };
  tasks.forEach((task, index) => {
    const submission = submissions[index];
    if (!submission) return;
    answers[task.id] = {
      value: submission.userInput,
      submitted: true,
      correct: submission.correct === true,
      grade: submission.grade,
      feedback: submission.feedback,
      attemptCount: submission.attemptCount,
      maxAttempts: submission.maxAttempts,
    };
  });

  const mainSettled = mainTasks.every((_, i) => isSettled(i));
  const tookBonus = bonusTasks.some(
    (_, j) => submissions[mainTasks.length + j]
  );
  const bonusSettled = bonusTasks.every((_, j) =>
    isSettled(mainTasks.length + j)
  );

  if (mainSettled && (bonusTasks.length === 0 || (tookBonus && bonusSettled))) {
    const record: WorksheetAnswerRecord = {
      answers: Object.fromEntries(
        Object.entries(answers).map(([taskId, answer]) => [
          taskId,
          {
            value: answer.value,
            correct: answer.correct,
            grade: answer.grade,
            feedback: answer.feedback,
          },
        ])
      ),
      doBonus: tookBonus,
    };
    return {
      kind: InteractiveElementStatusKind.Completed,
      score: {
        correct: mainTasks.filter((task) => answers[task.id]?.correct).length,
        total: mainTasks.length,
      },
      review: record,
      attempts: 1,
      completedAt: "",
    };
  }

  const firstOpenMain = mainTasks.findIndex((_, i) => !isSettled(i));
  const firstOpenBonus = bonusTasks.findIndex(
    (_, j) => !isSettled(mainTasks.length + j)
  );

  let partial: WorksheetRunnerState;
  if (firstOpenMain >= 0) {
    partial = {
      phase: "task",
      list: "main",
      currentIndex: firstOpenMain,
      answers,
      doBonus: false,
    };
  } else if (!tookBonus) {
    // Every main task is done and the additional tasks were not taken on.
    partial = {
      phase: "bonus-offer",
      list: "main",
      currentIndex: mainTasks.length - 1,
      answers,
      doBonus: false,
    };
  } else {
    partial = {
      phase: "task",
      list: "bonus",
      currentIndex: firstOpenBonus,
      answers,
      doBonus: true,
    };
  }

  return {
    kind: InteractiveElementStatusKind.InProgress,
    partial,
    lastTaskIndex: partial.currentIndex,
  };
};
